import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Pencil, RefreshCw } from 'lucide-react';
import { apiRequest, ApiError, openApiDocument } from '../lib/api';
import { useToast } from '../lib/toast';
import { useAuthStore } from '../lib/auth-store';
import { useConfirm } from '../components/ui/Confirm';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/Skeleton';
import { GatedSection } from '../components/shared/GatedSection';
import { DetailHeader } from '../components/shared/DetailHeader';
import { KpiStrip, type KpiItem } from '../components/shared/KpiStrip';
import { ActivityPanel } from '../components/shared/ActivityPanel';
import { FeeStatusBadge, ClaimStatusBadge } from '../components/finance/StatusBadge';
import { MarkPaidDrawer } from '../components/finance/MarkPaidDrawer';
import { formatINR, formatDate } from '../lib/format';
import {
  cycleLabel,
  type FeeCycle,
  type FeePayment,
  type FeePaymentClaim,
} from '../components/finance/types';

export const Route = createFileRoute('/_authed/finance/fees/payments/$paymentId')({
  component: FeePaymentDetailPage,
});

// ---------------------------------------------------------------------------
// Wire shapes — backend agent enriches the detail GET per §4.14
// ---------------------------------------------------------------------------

interface StudentEmbed {
  id: string;
  name: string;
  admissionNumber: string | null;
  householdId: string | null;
}

interface HouseholdEmbed {
  id: string;
  name: string;
}

interface ReceiptInfo {
  number: string | null;
  generatedAt?: string | null;
  lastRegeneratedAt?: string | null;
}

interface FeePaymentDetail {
  payment: FeePayment;
  student: StudentEmbed;
  cycle: FeeCycle;
  household?: HouseholdEmbed | null;
  confirmedByName?: string | null;
  claim?: FeePaymentClaim | null;
  receipt?: ReceiptInfo | null;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function FeePaymentDetailPage(): JSX.Element {
  const { paymentId } = Route.useParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const role = useAuthStore((s) => s.user?.primaryRole ?? null);
  const isAdmin = role === 'super_admin' || role === 'finance_admin';

  const [markPaidOpen, setMarkPaidOpen] = useState(false);

  const detailQuery = useQuery({
    queryKey: ['fee-payment', 'detail', paymentId] as const,
    queryFn: ({ signal }) =>
      apiRequest<FeePaymentDetail>(`/api/fees/payments/${encodeURIComponent(paymentId)}`, {
        signal,
      }),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && (err.status === 404 || err.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });

  const detail = detailQuery.data;

  const invalidatePayment = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['fee-payment', 'detail', paymentId] });
    void queryClient.invalidateQueries({ queryKey: ['feePayments'] });
    void queryClient.invalidateQueries({ queryKey: ['fee-cycle', 'payments'] });
    void queryClient.invalidateQueries({ queryKey: ['fee-cycle', 'detail'] });
  };

  const regenerateMutation = useMutation({
    mutationFn: () =>
      apiRequest<{ ok: true }>(
        `/api/fees/payments/${encodeURIComponent(paymentId)}/receipt/regenerate`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      toast('Receipt regeneration requested.', { variant: 'success' });
      invalidatePayment();
    },
    onError: (err: unknown) => {
      toast(err instanceof ApiError ? err.message : 'Could not regenerate receipt.', {
        variant: 'error',
      });
    },
  });

  const onRegenerate = async (): Promise<void> => {
    const ok = await confirm({
      title: 'Regenerate the receipt PDF?',
      description:
        'A new PDF will replace the existing one. The receipt number stays the same.',
      confirmLabel: 'Regenerate',
    });
    if (!ok) return;
    regenerateMutation.mutate();
  };

  const openReceipt = async (): Promise<void> => {
    try {
      await openApiDocument(`/api/fees/payments/${encodeURIComponent(paymentId)}/receipt-url`);
    } catch (err) {
      if (err instanceof ApiError) toast(err.message, { variant: 'error' });
      else toast('Could not open receipt.', { variant: 'error' });
    }
  };

  const kpiItems = useMemo<KpiItem[]>(() => {
    if (!detail) return [];
    const p = detail.payment;
    const daysVsDue = computeDaysVsDue(p);
    return [
      { label: 'Net amount', value: formatINR(p.netAmountPaise) },
      {
        label: 'Discount',
        value: p.discountPaise > 0 ? formatINR(p.discountPaise) : '—',
      },
      {
        label: 'Days vs due',
        value: daysVsDue.label,
        sub: daysVsDue.sub,
      },
      {
        label: 'Receipt #',
        value: p.receiptNumber ?? detail.receipt?.number ?? '—',
      },
    ];
  }, [detail]);

  const isPaid = detail?.payment.status === 'paid';
  const canEdit = isAdmin && detail && detail.payment.status !== 'paid';

  return (
    <div className="px-4 sm:px-6 py-6 max-w-screen-2xl mx-auto w-full">
      <GatedSection
        error={detailQuery.error}
        title="Fee payment"
        onRetry={() => detailQuery.refetch()}
      >
        {detailQuery.isLoading || !detail ? (
          <div className="space-y-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <div className="space-y-6">
            <DetailHeader
              title={`${detail.student.name} — ${cycleLabel(detail.cycle.year, detail.cycle.month)}`}
              sublabel={
                isPaid && detail.payment.paidAt ? (
                  <>
                    <span>
                      Confirmed by {detail.confirmedByName ?? detail.payment.confirmedBy ?? 'system'}
                    </span>
                    <span aria-hidden="true">·</span>
                    <span className="tabular-nums">
                      {formatDate(detail.payment.paidAt, { withTime: true })}
                    </span>
                  </>
                ) : (
                  <span className="tabular-nums">
                    Due {formatDate(`${detail.payment.dueDate}T00:00:00Z`)}
                  </span>
                )
              }
              statusChip={<FeeStatusBadge status={detail.payment.status} />}
              backTo="/finance/fees"
              actions={
                <>
                  {canEdit ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<Pencil size={14} aria-hidden="true" />}
                      onClick={() => setMarkPaidOpen(true)}
                    >
                      Edit
                    </Button>
                  ) : null}
                  {isPaid ? (
                    <Link
                      to="/finance/receipts/$paymentId"
                      params={{ paymentId: detail.payment.id }}
                      className="inline-flex items-center gap-1.5 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-3 py-1.5 text-sm font-medium text-[color:var(--color-text)] hover:border-gold-500"
                    >
                      <FileText size={14} aria-hidden="true" />
                      View receipt
                    </Link>
                  ) : null}
                  {isAdmin && isPaid ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<RefreshCw size={14} aria-hidden="true" />}
                      loading={regenerateMutation.isPending}
                      onClick={() => {
                        void onRegenerate();
                      }}
                    >
                      Regenerate receipt
                    </Button>
                  ) : null}
                </>
              }
            />

            <KpiStrip items={kpiItems} />

            <div className="grid gap-4 lg:grid-cols-2">
              <PaymentDetailsCard detail={detail} />
              <FeeCycleCard cycle={detail.cycle} />
              <StudentCard student={detail.student} />
              <HouseholdCard household={detail.household ?? null} />
              {detail.claim ? <ClaimCard claim={detail.claim} /> : null}
              <ReceiptCard
                detail={detail}
                onOpenReceipt={openReceipt}
                onRegenerate={isAdmin ? onRegenerate : undefined}
                regeneratePending={regenerateMutation.isPending}
              />
            </div>

            <ActivityPanel
              targetType="fee_payment"
              targetId={detail.payment.id}
              title="Activity"
            />
          </div>
        )}
      </GatedSection>

      {isAdmin && detail ? (
        <MarkPaidDrawer
          payment={markPaidOpen ? detail.payment : null}
          onClose={() => setMarkPaidOpen(false)}
          onMarked={() => {
            setMarkPaidOpen(false);
            invalidatePayment();
            toast('Payment marked as paid.', { variant: 'success' });
          }}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cards
// ---------------------------------------------------------------------------

function PaymentDetailsCard({ detail }: { detail: FeePaymentDetail }): JSX.Element {
  const p = detail.payment;
  return (
    <SectionCard title="Payment details">
      <Field label="Status">
        <FeeStatusBadge status={p.status} />
      </Field>
      <Field label="Amount" value={formatINR(p.amountPaise)} />
      <Field
        label="Discount"
        value={p.discountPaise > 0 ? formatINR(p.discountPaise) : '—'}
      />
      <Field label="Net amount" value={formatINR(p.netAmountPaise)} highlight />
      <Field label="Due date" value={formatDate(`${p.dueDate}T00:00:00Z`)} />
      <Field
        label="Paid via"
        value={p.paidVia ? formatPaidVia(p.paidVia) : '—'}
      />
      <Field
        label="Paid at"
        value={p.paidAt ? formatDate(p.paidAt, { withTime: true }) : '—'}
      />
      <Field label="Reference" value={p.paidReference ?? '—'} mono />
      <Field
        label="Provider"
        value={p.provider === 'razorpay' ? 'Razorpay' : 'Manual'}
      />
      {p.notes ? <Field label="Notes" value={p.notes} multiline /> : null}
    </SectionCard>
  );
}

function FeeCycleCard({ cycle }: { cycle: FeeCycle }): JSX.Element {
  return (
    <SectionCard title="Fee cycle">
      <p className="text-sm font-medium text-[color:var(--color-text)]">
        {cycleLabel(cycle.year, cycle.month)}
      </p>
      <p className="text-xs text-[color:var(--color-text-muted)]">
        Due {formatDate(`${cycle.dueDate}T00:00:00Z`)} · status: {cycle.status}
      </p>
      {cycle.notes ? (
        <p className="mt-2 text-sm text-[color:var(--color-text)] whitespace-pre-wrap">
          {cycle.notes}
        </p>
      ) : null}
      <div className="mt-3">
        <Link
          to="/finance/fees/$cycleId"
          params={{ cycleId: cycle.id }}
          className="text-xs text-gold-700 underline hover:no-underline"
        >
          Open cycle
        </Link>
      </div>
    </SectionCard>
  );
}

function StudentCard({ student }: { student: StudentEmbed }): JSX.Element {
  return (
    <SectionCard title="Student">
      <p className="text-sm font-medium text-[color:var(--color-text)]">{student.name}</p>
      {student.admissionNumber ? (
        <p className="text-xs text-[color:var(--color-text-muted)]">
          Admission #: {student.admissionNumber}
        </p>
      ) : null}
      <div className="mt-3">
        <Link
          to="/people/students/$studentId"
          params={{ studentId: student.id }}
          className="text-xs text-gold-700 underline hover:no-underline"
        >
          Open student profile
        </Link>
      </div>
    </SectionCard>
  );
}

function HouseholdCard({
  household,
}: {
  household: HouseholdEmbed | null;
}): JSX.Element {
  if (!household) {
    return (
      <SectionCard title="Family">
        <p className="text-sm text-[color:var(--color-text-muted)]">
          Family details unavailable.
        </p>
      </SectionCard>
    );
  }
  return (
    <SectionCard title="Family">
      <p className="text-sm font-medium text-[color:var(--color-text)]">{household.name}</p>
      <div className="mt-3">
        <Link
          to="/people/households/$householdId"
          params={{ householdId: household.id }}
          className="text-xs text-gold-700 underline hover:no-underline"
        >
          Open family
        </Link>
      </div>
    </SectionCard>
  );
}

function ClaimCard({ claim }: { claim: FeePaymentClaim }): JSX.Element {
  return (
    <SectionCard title="Claim">
      <div className="flex items-center gap-2">
        <ClaimStatusBadge status={claim.status} />
        <span className="text-xs text-[color:var(--color-text-muted)]">
          Submitted {formatDate(claim.createdAt, { withTime: true })}
        </span>
      </div>
      {claim.reference ? (
        <p className="mt-2 text-xs text-[color:var(--color-text-muted)]">
          Reference: <span className="font-mono">{claim.reference}</span>
        </p>
      ) : null}
      {claim.notes ? (
        <p className="mt-2 text-sm text-[color:var(--color-text)] whitespace-pre-wrap">
          {claim.notes}
        </p>
      ) : null}
      {claim.rejectedReason ? (
        <p className="mt-2 text-sm text-[color:var(--color-danger)] whitespace-pre-wrap">
          Rejected: {claim.rejectedReason}
        </p>
      ) : null}
      <div className="mt-3">
        <Link
          to="/finance/fees/claims/$claimId"
          params={{ claimId: claim.id }}
          className="text-xs text-gold-700 underline hover:no-underline"
        >
          View claim
        </Link>
      </div>
    </SectionCard>
  );
}

interface ReceiptCardProps {
  detail: FeePaymentDetail;
  onOpenReceipt: () => Promise<void>;
  onRegenerate?: (() => Promise<void>) | undefined;
  regeneratePending: boolean;
}

function ReceiptCard({
  detail,
  onOpenReceipt,
  onRegenerate,
  regeneratePending,
}: ReceiptCardProps): JSX.Element {
  const number = detail.payment.receiptNumber ?? detail.receipt?.number ?? null;
  const lastRegenerated = detail.receipt?.lastRegeneratedAt ?? null;
  const isPaid = detail.payment.status === 'paid';
  return (
    <SectionCard title="Receipt">
      <p className="text-sm font-medium text-[color:var(--color-text)]">
        {number ?? '—'}
      </p>
      {lastRegenerated ? (
        <p className="text-xs text-[color:var(--color-text-muted)]">
          Last regenerated {formatDate(lastRegenerated, { withTime: true })}
        </p>
      ) : null}
      {!isPaid ? (
        <p className="mt-2 text-xs text-[color:var(--color-text-muted)]">
          Receipts become available once the payment is marked paid.
        </p>
      ) : (
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="secondary"
            icon={<FileText size={14} aria-hidden="true" />}
            onClick={() => {
              void onOpenReceipt();
            }}
          >
            Download PDF
          </Button>
          {onRegenerate ? (
            <Button
              size="sm"
              variant="ghost"
              icon={<RefreshCw size={14} aria-hidden="true" />}
              loading={regeneratePending}
              onClick={() => {
                void onRegenerate();
              }}
            >
              Regenerate
            </Button>
          ) : null}
        </div>
      )}
    </SectionCard>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function SectionCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
        {title}
      </h3>
      <div className="mt-3 space-y-2 text-sm">{children}</div>
    </section>
  );
}

interface FieldProps {
  label: string;
  value?: string;
  children?: React.ReactNode;
  mono?: boolean;
  multiline?: boolean;
  highlight?: boolean;
}

function Field({
  label,
  value,
  children,
  mono = false,
  multiline = false,
  highlight = false,
}: FieldProps): JSX.Element {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <span className="text-xs text-[color:var(--color-text-muted)]">{label}</span>
      <span
        className={`min-w-0 text-right ${mono ? 'font-mono text-xs' : ''} ${
          multiline ? 'whitespace-pre-wrap text-left' : ''
        } ${highlight ? 'font-semibold text-[color:var(--color-text)]' : 'text-[color:var(--color-text)]'}`}
      >
        {children ?? value}
      </span>
    </div>
  );
}

function formatPaidVia(via: NonNullable<FeePayment['paidVia']>): string {
  switch (via) {
    case 'cash':
      return 'Cash';
    case 'bank_transfer':
      return 'Bank transfer';
    case 'upi_offline':
      return 'UPI (offline)';
    case 'cheque':
      return 'Cheque';
    case 'razorpay':
      return 'Razorpay';
    case 'other':
      return 'Other';
    default:
      return via;
  }
}

function computeDaysVsDue(payment: FeePayment): { label: string; sub?: string | undefined } {
  // For paid payments, compare paidAt to dueDate so we can show "on time / N late".
  // For unpaid, compare today to dueDate so we show how soon/overdue.
  const due = new Date(`${payment.dueDate}T00:00:00Z`).getTime();
  if (Number.isNaN(due)) return { label: '—' };
  const reference = payment.paidAt ? new Date(payment.paidAt).getTime() : Date.now();
  if (Number.isNaN(reference)) return { label: '—' };
  const days = Math.round((reference - due) / (1000 * 60 * 60 * 24));
  if (payment.paidAt) {
    if (days <= 0) {
      return { label: 'On time', sub: days < 0 ? `${Math.abs(days)}d early` : undefined };
    }
    return { label: `${days}d late`, sub: 'after due' };
  }
  if (days <= 0) {
    return { label: `${Math.abs(days)}d`, sub: 'until due' };
  }
  return { label: `${days}d`, sub: 'overdue' };
}
